<template>
    <div class="app-progress-page">
      <!-- Background Elements -->
      <div class="page-grid"></div>
      <div class="page-orb orb-1"></div>
      <div class="page-orb orb-2"></div>
  
      <!-- Top Navigation -->
      <div class="top-nav">
        <div class="nav-container">
          <div class="nav-brand">
            <div class="nav-hex"></div>
            <span>MANAGE APP PROGRESS</span>
          </div>
          <button class="nav-btn" @click="goBack">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15.8333 10H4.16667M4.16667 10L10 15.8333M4.16667 10L10 4.16667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Back to Settings
          </button>
        </div>
      </div>
  
      <!-- Main Content -->
      <div class="progress-container">
        <!-- Hero Section -->
        <div class="progress-hero">
          <div class="hero-text">
            <h1 class="hero-title">Update Company Journeys</h1>
            <p class="hero-subtitle">Pick a company, review their journey, and set the next stage</p>
          </div>
          <div class="stats-card">
            <div class="stat-item">
              <div class="stat-label">Companies</div>
              <div class="stat-value">{{ companies.length }}</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="stat-label">Completed</div>
              <div class="stat-value">{{ completedCount }}</div>
            </div>
          </div>
        </div>
  
        <!-- Main Card -->
        <div class="main-card">
          <h2 class="card-title">Select Company and Update Progress</h2>
  
          <!-- Company Search/Select -->
          <div class="customer-select-section">
            <div class="search-wrapper">
              <svg class="search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search by company name or owner email..."
                @focus="showDropdown = true"
                @input="showDropdown = true"
              />
              <button v-if="searchQuery" class="clear-btn" @click="clearSearch">✕</button>
            </div>
  
            <!-- Dropdown Results -->
            <div v-if="showDropdown && filteredCompanies.length > 0" class="customer-dropdown">
              <div
                v-for="company in filteredCompanies"
                :key="company.id"
                class="customer-option"
                @click="selectCompany(company)"
              >
                <div class="customer-avatar">{{ company.initials }}</div>
                <div class="customer-info">
                  <div class="customer-name">{{ company.businessName || '(No name)' }}</div>
                  <div class="customer-email">{{ company.ownerEmail }}</div>
                </div>
                <div :class="['progress-badge', `badge-${getStageClass(company.appProgress)}`]">
                  {{ company.appProgress }}
                </div>
              </div>
            </div>
  
            <!-- No Results -->
            <div v-else-if="showDropdown && searchQuery && filteredCompanies.length === 0" class="no-results">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <path d="M24 44C35.0457 44 44 35.0457 44 24C44 12.9543 35.0457 4 24 4C12.9543 4 4 12.9543 4 24C4 35.0457 12.9543 44 24 44Z" stroke="currentColor" stroke-width="3"/>
                <path d="M16 16L32 32M32 16L16 32" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
              </svg>
              <p>No companies found</p>
            </div>
          </div>
  
          <!-- Info Alert (No Company Selected) -->
          <div v-if="!selected" class="info-alert">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 18.3333C14.6024 18.3333 18.3334 14.6024 18.3334 10C18.3334 5.39763 14.6024 1.66667 10 1.66667C5.39765 1.66667 1.66669 5.39763 1.66669 10C1.66669 14.6024 5.39765 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5"/>
              <path d="M10 6.66667V10M10 13.3333H10.0083" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <span>Pick a company above to see their journey</span>
          </div>
  
          <!-- Selected Company Summary -->
          <transition name="slide-down">
            <div v-if="selected" class="customer-summary">
              <div class="summary-left">
                <div class="summary-avatar">{{ selected.initials }}</div>
                <div class="summary-info">
                  <div class="summary-label">Company</div>
                  <div class="summary-name">{{ selected.businessName || selected.ownerEmail }}</div>
                </div>
              </div>
              <div class="summary-badges">
                <div :class="['current-badge', `badge-${getStageClass(selected.appProgress)}`]">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M14 8L8 14L2 8M8 2V14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  {{ selected.appProgress }}
                </div>
                <div v-if="nextStep" class="next-badge">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 14L14 8M14 8L8 2M14 8H2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Next: {{ nextStep }}
                </div>
              </div>
              <div class="summary-date">
                Last updated: {{ lastStageDate || '—' }}
              </div>
            </div>
          </transition>
  
          <!-- Timeline Journey -->
          <transition name="fade">
            <div v-if="selected" class="timeline-section">
              <h3 class="timeline-title">App Progress Journey</h3>
              <div class="timeline">
                <div
                  v-for="(step, index) in progressOptions"
                  :key="step"
                  :class="['timeline-item', { completed: isStepComplete(step) }]"
                >
                  <div class="timeline-marker">
                    <svg v-if="isStepComplete(step)" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2"/>
                    </svg>
                  </div>
                  <div v-if="index < progressOptions.length - 1" class="timeline-line"></div>
                  <div class="timeline-content">
                    <div class="timeline-step-name">{{ step }}</div>
                    <div class="timeline-date">{{ stageDate(step) || (isStepComplete(step) ? '—' : '') }}</div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
  
          <!-- Stage Selection -->
          <transition name="fade">
            <div v-if="selected" class="stage-section">
              <div class="quick-select">
                <span class="quick-label">Quick set:</span>
                <div class="stage-chips">
                  <button
                    v-for="stage in progressOptions"
                    :key="stage"
                    :class="['stage-chip', `chip-${getStageClass(stage)}`, { active: newProgress === stage }]"
                    @click="newProgress = stage"
                  >
                    {{ stage }}
                  </button>
                </div>
              </div>
  
              <div class="stage-select-wrapper">
                <label>Or choose a new stage</label>
                <select v-model="newProgress" class="stage-select">
                  <option value="">Select stage...</option>
                  <option v-for="stage in progressOptions" :key="stage" :value="stage">
                    {{ stage }}
                  </option>
                </select>
              </div>

              <p class="email-note">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2 4.5L8 9L14 4.5M2.5 3H13.5C14.0523 3 14.5 3.44772 14.5 4V12C14.5 12.5523 14.0523 13 13.5 13H2.5C1.94772 13 1.5 12.5523 1.5 12V4C1.5 3.44772 1.94772 3 2.5 3Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                The company's owner gets an email automatically when this stage changes.
              </p>
            </div>
          </transition>
  
          <!-- Update Button -->
          <button
            class="btn-update"
            :disabled="!selected || !newProgress || newProgress === selected?.appProgress || updating"
            @click="updateProgress"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C12.5 2.5 14.75 3.75 16.25 5.625M16.25 5.625V2.5M16.25 5.625H13.125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            {{ updating ? 'Updating...' : 'Update App Progress' }}
          </button>
  
          <!-- Message Alert -->
          <transition name="fade">
            <div v-if="message" :class="['message-alert', messageType]">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path v-if="messageType === 'success'" d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path v-else d="M10 18.3333C14.6024 18.3333 18.3334 14.6024 18.3334 10C18.3334 5.39763 14.6024 1.66667 10 1.66667C5.39765 1.66667 1.66669 5.39763 1.66669 10C1.66669 14.6024 5.39765 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5"/>
              </svg>
              <span>{{ message }}</span>
              <button @click="message = ''">✕</button>
            </div>
          </transition>
        </div>
      </div>
  
      <!-- Snackbar -->
      <div v-if="snack.show" class="snackbar">
        <span>{{ snack.msg }}</span>
        <button @click="snack.show = false">✕</button>
      </div>
    </div>
  </template>

<script>
  import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
  import { db } from '../firebase'
  import {
    collection, getDocs, updateDoc, doc, serverTimestamp
  } from 'firebase/firestore'
  import router from '../router'

  export default {
    name: 'ManageAppProgressView',
    setup() {
      const companies = ref([])
      const loadingCompanies = ref(true)
      const selectedId = ref(null)
      const searchQuery = ref('')
      const showDropdown = ref(false)
      const newProgress = ref('')
      const updating = ref(false)
      const message = ref('')
      const messageType = ref('success')
      const snack = ref({ show: false, msg: '' })

      // Matches the journey shown on the customer's own Dashboard
      // (CustomerDashboardView.vue) and staff's per-company detail page
      // (CustomerDetailView.vue) — Corporate Connectionz is a shared
      // platform being configured per company, not a custom-built app, so
      // these track account setup rather than development milestones.
      const progressOptions = ['Not Started', 'Designing', 'Developing', 'Testing', 'Completed']

      const selected = computed(() => companies.value.find(c => c.id === selectedId.value) || null)

      const filteredCompanies = computed(() => {
        const q = searchQuery.value.toLowerCase().trim()
        if (!q) return companies.value
        return companies.value.filter(c =>
          (c.businessName || '').toLowerCase().includes(q) ||
          (c.ownerEmail || '').toLowerCase().includes(q)
        )
      })

      const progressIndex = computed(() =>
        selected.value ? progressOptions.indexOf(selected.value.appProgress) : -1
      )

      const nextStep = computed(() => {
        if (progressIndex.value < 0) return ''
        const nxt = progressOptions[progressIndex.value + 1]
        return nxt || ''
      })

      const completedCount = computed(() =>
        companies.value.filter(c => c.appProgress === 'Completed').length
      )

      const lastStageDate = computed(() => {
        if (!selected.value) return ''
        const key = selected.value.appProgress
        return stageDate(key)
      })

      function initialsFrom(name = '', email = '') {
        const nm = (name || '').trim()
        if (nm) return nm.split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase()
        return (email.split('@')[0] || '??').slice(0, 2).toUpperCase()
      }

      function getStageClass(stage) {
        switch ((stage || '').toLowerCase()) {
          case 'not started': return 'not-started'
          case 'designing': return 'designing'
          case 'developing': return 'developing'
          case 'testing': return 'testing'
          case 'completed': return 'completed'
          default: return 'default'
        }
      }

      function prettyDate(ts) {
        const d = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : (ts instanceof Date ? ts : null))
        return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''
      }

      function stageDate(step) {
        if (!selected.value?.appProgressDates) return ''
        return prettyDate(selected.value.appProgressDates[step])
      }

      function isStepComplete(step) {
        if (!selected.value) return false
        return progressOptions.indexOf(step) <= progressOptions.indexOf(selected.value.appProgress || 'Not Started')
      }

      async function fetchCompanies() {
        loadingCompanies.value = true
        try {
          // Reads customers/{companyId} directly — NOT users filtered by
          // role — since appProgress lives on the shared company record,
          // not any individual person's own account. A company can have
          // many employee logins; only one of them is even the "owner",
          // and appProgress isn't tied to that owner's uid at all.
          const snap = await getDocs(collection(db, 'customers'))
          companies.value = snap.docs.map(d => {
            const data = d.data() || {}
            return {
              id: d.id,
              businessName: data.businessName || '',
              ownerEmail: data.ownerEmail || '',
              initials: initialsFrom(data.businessName, data.ownerEmail),
              appProgress: data.appProgress || 'Not Started',
              appProgressDates: data.appProgressDates || {}
            }
          })
        } catch (e) {
          console.error('fetchCompanies', e)
          message.value = '❌ Failed to load companies.'
          messageType.value = 'error'
        } finally {
          loadingCompanies.value = false
        }
      }

      function selectCompany(company) {
        selectedId.value = company.id
        searchQuery.value = company.businessName || company.ownerEmail
        showDropdown.value = false
        newProgress.value = ''
        message.value = ''
      }

      function clearSearch() {
        searchQuery.value = ''
        selectedId.value = null
        showDropdown.value = false
        newProgress.value = ''
      }

      async function updateProgress() {
        if (!selected.value || !newProgress.value) return
        updating.value = true
        try {
          // Single write to the company's own record — the old version of
          // this page wrote to both users/{uid} and customers/{uid} using
          // the customer's own uid as if it were the company id, which
          // isn't how the data model works (a company can have many
          // employee logins, so appProgress can't live on any one of
          // them). The email to the company's owner is NOT sent from
          // here — see the notifyProgressChange Firestore trigger in
          // index.ts, which fires on this same write and also covers
          // CustomerDetailView.vue's own stage editor, so the alert can't
          // be forgotten by whichever screen made the change.
          await updateDoc(doc(db, 'customers', selected.value.id), {
            appProgress: newProgress.value,
            [`appProgressDates.${newProgress.value}`]: serverTimestamp()
          })

          snack.value = { show: true, msg: '✓ Progress updated!' }
          message.value = '✅ App progress updated — the company owner has been emailed.'
          messageType.value = 'success'

          await fetchCompanies()
          selectedId.value = selected.value.id
          searchQuery.value = selected.value.businessName || selected.value.ownerEmail
          newProgress.value = ''
        } catch (e) {
          console.error('updateProgress', e)
          message.value = '❌ Failed to update app progress.'
          messageType.value = 'error'
        } finally {
          updating.value = false
        }
      }

      function goBack() {
        router.push('/admin-settings')
      }

      function handleClickOutside(e) {
        const searchWrapper = document.querySelector('.search-wrapper')
        const dropdown = document.querySelector('.customer-dropdown')
        if (searchWrapper && !searchWrapper.contains(e.target) && dropdown && !dropdown.contains(e.target)) {
          showDropdown.value = false
        }
      }

      onMounted(() => {
        fetchCompanies()
        document.addEventListener('click', handleClickOutside)
      })

      onBeforeUnmount(() => {
        document.removeEventListener('click', handleClickOutside)
      })

      return {
        companies,
        loadingCompanies,
        selectedId,
        searchQuery,
        showDropdown,
        newProgress,
        updating,
        message,
        messageType,
        snack,
        progressOptions,
        selected,
        filteredCompanies,
        progressIndex,
        nextStep,
        completedCount,
        lastStageDate,
        getStageClass,
        stageDate,
        isStepComplete,
        selectCompany,
        clearSearch,
        updateProgress,
        goBack
      }
    }
  }
</script>

<style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@500;600;700;800&family=Cormorant+Garamond:wght@400;500;600&display=swap');

  .app-progress-page {
    --bg-page: #08090a;
    --bg-alt: #0f1012;
    --bg-card: #0f1012;
    --bg-card-hover: #17181c;
    --border-subtle: rgba(255, 255, 255, 0.09);
    --border-gold: rgba(201, 168, 76, 0.35);
    --text-primary: #F5F0E8;
    --text-secondary: rgba(245, 240, 232, 0.68);
    --text-muted: rgba(245, 240, 232, 0.48);
    --gold: #C9A84C;
    --gold-light: #F0D080;
    --gold-dark: #9C7A34;
    --graphite: #6A6D74;
    --graphite-dark: #3A3D44;
    --bronze: #9C7A34;

    min-height: 100vh;
    background: linear-gradient(160deg, #101114 0%, #08090a 55%, #050506 100%);
    position: relative;
    font-family: 'Syne', -apple-system, BlinkMacSystemFont, sans-serif;
    color: var(--text-primary);
    padding-bottom: 80px;
    overflow-x: hidden;
  }

  .app-progress-page .hero-title,
  .app-progress-page .card-title,
  .app-progress-page .timeline-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    letter-spacing: -0.005em;
  }

  /* ==================== BACKGROUND ELEMENTS ==================== */
  .page-grid {
    position: fixed;
    inset: 0;
    background-image:
      linear-gradient(to right, rgba(255, 255, 255, 0.06) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  .page-orb {
    position: fixed;
    border-radius: 50%;
    filter: blur(140px);
    opacity: 0.2;
    pointer-events: none;
    z-index: 0;
  }

  .orb-1 {
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(240, 208, 128, 0.5), transparent);
    top: -200px;
    right: -200px;
  }

  .orb-2 {
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(74, 77, 84, 0.35), transparent);
    bottom: -150px;
    left: -150px;
  }

  /* ==================== TOP NAVIGATION ==================== */
  .top-nav {
    position: sticky;
    top: 0;
    z-index: 100;
    background: rgba(8, 9, 10, 0.88);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border-subtle);
  }

  .nav-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 16px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .nav-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--text-primary);
    font-size: 13.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
  }

  .nav-hex {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    background: linear-gradient(150deg, var(--gold-light) 0%, var(--gold) 55%, var(--gold-dark) 100%);
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  }

  .nav-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: 'Syne', sans-serif;
  }

  .nav-btn:hover {
    background: rgba(201, 168, 76, 0.08);
    border-color: var(--border-gold);
  }

  /* ==================== PROGRESS CONTAINER ==================== */
  .progress-container {
    position: relative;
    z-index: 1;
    max-width: 900px;
    margin: 0 auto;
    padding: 0 24px;
  }

  /* ==================== HERO SECTION ==================== */
  .progress-hero {
    padding: 48px 0 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
  }

  .hero-text {
    flex: 1;
    min-width: 300px;
  }

  .hero-title {
    font-size: clamp(28px, 5vw, 38px);
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 8px;
    line-height: 1.15;
  }

  .hero-subtitle {
    font-size: 15px;
    color: var(--text-secondary);
    line-height: 1.6;
  }

  .stats-card {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 18px 24px;
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 14px;
  }

  .stat-item { text-align: center; }
  .stat-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-muted); margin-bottom: 4px; }
  .stat-value { font-size: 24px; font-weight: 700; color: var(--gold-light); }
  .stat-divider { width: 1px; height: 32px; background: var(--border-subtle); }

  /* ==================== MAIN CARD ==================== */
  .main-card {
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 18px;
    padding: 32px;
  }

  .card-title {
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 24px;
  }

  /* ==================== SEARCH / DROPDOWN ==================== */
  .customer-select-section {
    position: relative;
    margin-bottom: 24px;
  }

  .search-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  .search-icon {
    position: absolute;
    left: 16px;
    color: var(--text-muted);
    pointer-events: none;
  }

  .search-wrapper input {
    width: 100%;
    padding: 14px 44px;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 12px;
    color: var(--text-primary);
    font-family: 'Syne', sans-serif;
    font-size: 14.5px;
    outline: none;
    transition: border-color 0.2s ease;
  }

  .search-wrapper input:focus { border-color: var(--gold); }

  .clear-btn {
    position: absolute;
    right: 14px;
    background: transparent;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    font-size: 16px;
  }

  .customer-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background: #0f1012;
    border: 1px solid var(--border-gold);
    border-radius: 14px;
    max-height: 320px;
    overflow-y: auto;
    z-index: 50;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  }

  .customer-option {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    cursor: pointer;
    border-bottom: 1px solid var(--border-subtle);
    transition: background 0.2s ease;
  }

  .customer-option:last-child { border-bottom: none; }
  .customer-option:hover { background: var(--bg-card-hover); }

  .customer-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(150deg, var(--gold-light), var(--gold));
    color: #08090A;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 12px;
    flex-shrink: 0;
  }

  .customer-info { flex: 1; min-width: 0; }
  .customer-name { font-size: 14px; font-weight: 700; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .customer-email { font-size: 12px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  .progress-badge {
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 700;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .badge-not-started { background: rgba(255, 255, 255, 0.06); color: var(--text-muted); }
  .badge-designing { background: rgba(74, 77, 84, 0.2); color: var(--graphite); }
  .badge-developing { background: rgba(201, 168, 76, 0.15); color: var(--gold-light); }
  .badge-testing { background: rgba(156, 122, 52, 0.2); color: var(--bronze); }
  .badge-completed { background: rgba(156, 122, 52, 0.22); color: var(--gold-light); }
  .badge-default { background: rgba(255, 255, 255, 0.06); color: var(--text-muted); }

  .no-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 40px 20px;
    color: var(--text-muted);
    text-align: center;
  }

  .no-results svg { color: var(--border-subtle); }

  /* ==================== INFO ALERT ==================== */
  .info-alert {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    color: var(--text-secondary);
    font-size: 13.5px;
  }

  /* ==================== SUMMARY ==================== */
  .customer-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 14px;
    padding: 18px;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 14px;
    margin-top: 20px;
  }

  .summary-left { display: flex; align-items: center; gap: 12px; }

  .summary-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: linear-gradient(150deg, var(--gold-light), var(--gold));
    color: #08090A;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 14px;
  }

  .summary-label { font-size: 11px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.06em; }
  .summary-name { font-size: 15px; font-weight: 700; color: var(--text-primary); }

  .summary-badges { display: flex; gap: 10px; flex-wrap: wrap; }

  .current-badge, .next-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
  }

  .current-badge.badge-not-started { background: rgba(255, 255, 255, 0.06); color: var(--text-muted); }
  .current-badge.badge-designing { background: rgba(74, 77, 84, 0.2); color: var(--graphite); }
  .current-badge.badge-developing { background: rgba(201, 168, 76, 0.15); color: var(--gold-light); }
  .current-badge.badge-testing { background: rgba(156, 122, 52, 0.2); color: var(--bronze); }
  .current-badge.badge-completed { background: rgba(156, 122, 52, 0.22); color: var(--gold-light); }

  .next-badge { background: rgba(255, 255, 255, 0.05); border: 1px solid var(--border-subtle); color: var(--text-secondary); }

  .summary-date { font-size: 12px; color: var(--text-muted); width: 100%; }

  /* ==================== TIMELINE ==================== */
  .timeline-section { margin-top: 28px; }

  .timeline-title {
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 18px;
  }

  .timeline { display: flex; flex-direction: column; }

  .timeline-item {
    display: flex;
    gap: 14px;
    position: relative;
    padding-bottom: 24px;
  }

  .timeline-item:last-child { padding-bottom: 0; }

  .timeline-marker {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-alt);
    border: 2px solid var(--border-subtle);
    color: var(--text-muted);
    flex-shrink: 0;
    z-index: 1;
  }

  .timeline-item.completed .timeline-marker {
    background: linear-gradient(150deg, var(--gold-light), var(--gold));
    border-color: var(--gold);
    color: #08090A;
  }

  .timeline-line {
    position: absolute;
    left: 13px;
    top: 28px;
    width: 2px;
    height: calc(100% - 28px);
    background: var(--border-subtle);
  }

  .timeline-item.completed .timeline-line { background: var(--gold); }

  .timeline-step-name { font-size: 14px; font-weight: 700; color: var(--text-primary); }
  .timeline-date { font-size: 11.5px; color: var(--text-muted); margin-top: 2px; }

  /* ==================== STAGE SELECTION ==================== */
  .stage-section { margin-top: 28px; }

  .quick-select { margin-bottom: 18px; }

  .quick-label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 10px;
  }

  .stage-chips { display: flex; flex-wrap: wrap; gap: 8px; }

  .stage-chip {
    padding: 9px 16px;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 999px;
    color: var(--text-secondary);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .stage-chip:hover { border-color: var(--border-gold); }

  .stage-chip.active {
    background: rgba(201, 168, 76, 0.15);
    border-color: var(--border-gold);
    color: var(--gold-light);
  }

  .stage-select-wrapper { margin-top: 6px; }

  .stage-select-wrapper label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: var(--text-muted);
    margin-bottom: 8px;
  }

  .stage-select {
    width: 100%;
    padding: 12px 14px;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    color: var(--text-primary);
    font-family: 'Syne', sans-serif;
    font-size: 14px;
  }

  .email-note {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 14px;
    font-size: 12.5px;
    color: var(--text-muted);
  }

  .email-note svg { color: var(--gold-light); flex-shrink: 0; }

  /* ==================== UPDATE BUTTON ==================== */
  .btn-update {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 15px;
    margin-top: 24px;
    background: linear-gradient(150deg, var(--gold-light), var(--gold));
    border: none;
    border-radius: 12px;
    color: #08090A;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-update:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(201, 168, 76, 0.3);
  }

  .btn-update:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ==================== MESSAGE ALERT ==================== */
  .message-alert {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    border-radius: 10px;
    margin-top: 18px;
    font-size: 13.5px;
  }

  .message-alert.success {
    background: rgba(156, 122, 52, 0.14);
    border: 1px solid rgba(156, 122, 52, 0.4);
    color: var(--gold-light);
  }

  .message-alert.error {
    background: rgba(220, 53, 69, 0.15);
    border: 1px solid rgba(220, 53, 69, 0.35);
    color: #E8969F;
  }

  .message-alert button {
    margin-left: auto;
    background: transparent;
    border: none;
    color: inherit;
    cursor: pointer;
    opacity: 0.7;
  }

  /* ==================== SNACKBAR ==================== */
  .snackbar {
    position: fixed;
    bottom: 28px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 20px;
    background: #0f1012;
    border: 1px solid var(--border-gold);
    border-radius: 12px;
    color: var(--text-primary);
    font-size: 14px;
    z-index: 600;
  }

  .snackbar button { background: transparent; border: none; color: var(--text-secondary); cursor: pointer; }

  /* ==================== TRANSITIONS ==================== */
  .fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
  .fade-enter, .fade-leave-to { opacity: 0; }
  .slide-down-enter-active, .slide-down-leave-active { transition: all 0.3s ease; }
  .slide-down-enter, .slide-down-leave-to { opacity: 0; transform: translateY(-10px); }

  /* ==================== RESPONSIVE ==================== */
  @media (max-width: 600px) {
    .progress-hero { flex-direction: column; align-items: flex-start; }
    .main-card { padding: 22px; }
  }
</style>